/**
 * Liniendiagramm fuer mehrere Reihen auf einer Achse (F-26), z. B. Schlaf,
 * Energie und Stimmung uebereinander.
 *
 * - Eine Achse, feste Spanne (bei Bewertungen 1-10), Luecken bleiben Luecken.
 * - Farben aus `--color-serie-N` (zentral, Leitlinie 7) - nie aus der
 *   Bewertungsskala, die gehoert den Werten.
 * - Legende ab zwei Reihen, Fadenkreuz mit allen Werten am Zeitpunkt (auch per
 *   Pfeiltasten), und eine Tabellenansicht - nichts haengt nur an Farbe oder
 *   am Ueberfahren mit der Maus.
 */
import { useEffect, useId, useRef, useState } from 'react';

import { cn } from '@/lib/utils/cn';

export interface LinienReihe {
  id: string;
  name: string;
  /** 0-basiert, `--color-serie-(platz+1)`. */
  platz: number;
  /** Eigene Farbe statt des Platzes - z. B. neutral fuer eine einzelne Reihe. */
  farbe?: string;
  /** Gestrichelt - fuer Hilfslinien wie Trend oder Zielwert, ohne Farbe unterscheidbar. */
  gestrichelt?: boolean;
  werte: (number | null)[];
}

const H = 220;
const RAND = { l: 28, r: 12, o: 10, u: 24 };

const farbeVon = (r: Pick<LinienReihe, 'platz' | 'farbe'>) => r.farbe ?? `var(--color-serie-${r.platz + 1})`;
const zahl = (v: number) => v.toLocaleString('de-DE', { maximumFractionDigits: 1 });

export function LinienDiagramm({ titel, punkte, reihen, min, max, ticks, einheit = '', umgekehrt = false }: {
  titel: string;
  /** Zeitpunkte: voller Titel fuer Tooltip/Tabelle, kurz fuer die Achse. */
  punkte: { key: string; titel: string; kurz: string }[];
  reihen: LinienReihe[];
  min: number;
  max: number;
  ticks: number[];
  einheit?: string;
  /** Kleinere Werte oben - z. B. Schulnoten 1-6, bei denen 1 die beste ist. */
  umgekehrt?: boolean;
}) {
  const [aktiv, setAktiv] = useState<number | null>(null);
  const [tabelle, setTabelle] = useState(false);
  const id = useId();
  // Breite in echten Pixeln: Schrift und Hoehe bleiben gleich, egal wie breit die Spalte ist.
  const huelle = useRef<HTMLDivElement>(null);
  const [B, setB] = useState(640);
  useEffect(() => {
    const el = huelle.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => { if (e && e.contentRect.width > 0) setB(Math.round(e.contentRect.width)); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [tabelle]);
  const n = punkte.length;
  const breite = B - RAND.l - RAND.r;
  const hoehe = H - RAND.o - RAND.u;
  const x = (i: number) => RAND.l + (n <= 1 ? breite / 2 : (i / (n - 1)) * breite);
  const y = (v: number) => RAND.o + (umgekehrt ? ((v - min) / (max - min)) * hoehe : hoehe - ((v - min) / (max - min)) * hoehe);

  // Luecken (null) unterbrechen die Linie.
  const pfad = (werte: (number | null)[]) => {
    let d = '';
    let offen = false;
    werte.forEach((v, i) => {
      if (v === null) { offen = false; return; }
      d += `${offen ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
      offen = true;
    });
    return d;
  };
  const einzeln = (werte: (number | null)[], i: number) => werte[i] !== null && werte[i - 1] == null && werte[i + 1] == null;
  const achsenIdx = n <= 1 ? [0] : [...new Set([0, Math.floor((n - 1) / 2), n - 1])];
  const tip = aktiv !== null ? punkte[aktiv] : undefined;

  return (
    <figure className="flex flex-col gap-2">
      <figcaption className="flex flex-wrap items-center gap-2 text-[12px] text-ink-muted">
        <span className="flex-1">{titel}</span>
        <button type="button" aria-pressed={tabelle} onClick={() => setTabelle((t) => !t)} className="rounded border border-line px-2 py-0.5 text-[11px] hover:text-ink">
          {tabelle ? 'Als Diagramm' : 'Als Tabelle'}
        </button>
      </figcaption>

      {reihen.length >= 2 && (
        <ul aria-label="Legende" className="flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-ink-muted">
          {reihen.map((r) => (
            <li key={r.id} className="flex items-center gap-1.5">
              {r.gestrichelt
                ? <span className="w-4 border-t-2 border-dashed" style={{ borderColor: farbeVon(r) }} aria-hidden="true" />
                : <span className="h-0.5 w-4 rounded-full" style={{ backgroundColor: farbeVon(r) }} aria-hidden="true" />}
              {r.name}
            </li>
          ))}
        </ul>
      )}

      {tabelle ? (
        <div className="max-h-72 overflow-auto">
          <table className="w-full text-[12px]">
            <thead className="sticky top-0 bg-surface text-ink-faint">
              <tr><th className="py-1 pr-3 text-left font-medium">Zeitpunkt</th>{reihen.map((r) => <th key={r.id} className="px-2 py-1 text-right font-medium">{r.name}</th>)}</tr>
            </thead>
            <tbody className="tabular-nums">
              {punkte.map((p, i) => (
                <tr key={p.key} className="border-t border-line">
                  <td className="py-1 pr-3 text-ink-muted">{p.titel}</td>
                  {reihen.map((r) => <td key={r.id} className="px-2 py-1 text-right">{r.werte[i] === null ? '–' : zahl(r.werte[i]!)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div ref={huelle} className="relative">
          <svg
            viewBox={`0 0 ${B} ${H}`}
            width={B} height={H}
            className="block max-w-full touch-none outline-none focus-visible:ring-2 focus-visible:ring-accent"
            role="img"
            aria-label={`${titel}. Werte in der Tabellenansicht.`}
            tabIndex={0}
            aria-describedby={tip ? `${id}-tip` : undefined}
            onPointerMove={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              const px = ((e.clientX - r.left) / r.width) * B;
              setAktiv(Math.max(0, Math.min(n - 1, Math.round(((px - RAND.l) / breite) * (n - 1)))));
            }}
            onPointerLeave={() => setAktiv(null)}
            onBlur={() => setAktiv(null)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') { e.preventDefault(); setAktiv((a) => Math.min(n - 1, (a ?? -1) + 1)); }
              if (e.key === 'ArrowLeft') { e.preventDefault(); setAktiv((a) => Math.max(0, (a ?? n) - 1)); }
            }}
          >
            {ticks.map((t) => (
              <g key={t}>
                <line x1={RAND.l} x2={B - RAND.r} y1={y(t)} y2={y(t)} stroke="var(--color-line)" strokeWidth={1} />
                <text x={RAND.l - 6} y={y(t) + 4} textAnchor="end" fontSize={11} fill="var(--color-ink-faint)" className="tabular-nums">{t}</text>
              </g>
            ))}
            {achsenIdx.map((i) => (
              <text key={i} x={x(i)} y={H - 6} fontSize={11} fill="var(--color-ink-faint)" textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}>{punkte[i]?.kurz}</text>
            ))}
            {aktiv !== null && <line x1={x(aktiv)} x2={x(aktiv)} y1={RAND.o} y2={RAND.o + hoehe} stroke="var(--color-line-strong)" strokeWidth={1} />}
            {reihen.map((r) => (
              <g key={r.id}>
                <path d={pfad(r.werte)} fill="none" stroke={farbeVon(r)} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" strokeDasharray={r.gestrichelt ? '6 5' : undefined} />
                {r.werte.map((v, i) => (v !== null && (einzeln(r.werte, i) || i === aktiv)) && (
                  <circle key={i} cx={x(i)} cy={y(v)} r={4} fill={farbeVon(r)} stroke="var(--color-surface)" strokeWidth={2} />
                ))}
              </g>
            ))}
          </svg>

          {tip && aktiv !== null && (
            <div
              id={`${id}-tip`}
              role="status"
              className={cn(
                'pointer-events-none absolute top-1 z-10 min-w-36 rounded-md border border-line bg-surface-raised px-2.5 py-1.5 text-[12px] shadow-lg',
                aktiv > n / 2 ? 'left-2' : 'right-2',
              )}
            >
              <p className="mb-1 text-[11px] text-ink-faint">{tip.titel}</p>
              {reihen.map((r) => (
                <p key={r.id} className="flex items-center gap-1.5">
                  <span className="h-0.5 w-3 rounded-full" style={{ backgroundColor: farbeVon(r) }} aria-hidden="true" />
                  <strong className="font-semibold tabular-nums text-ink">{r.werte[aktiv] === null ? '–' : `${zahl(r.werte[aktiv]!)}${einheit}`}</strong>
                  <span className="text-ink-muted">{r.name}</span>
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </figure>
  );
}
